import React, { useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Popover, Stack, TextField, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';
import { MIS_UI } from '../../utils/misConstants';

const MultiSelectFilterButton = ({
  label,
  options = [],
  value = [],
  onChange,
  disabled = false,
  searchable = false,
}) => {
  const { misDashboard: m } = useTranslatedMessages();
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const open = Boolean(anchorEl);
  const selectedSet = useMemo(() => new Set(value), [value]);
  const hasSelection = value.length > 0;

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = !term
      ? options
      : options.filter(
          (option) =>
            option.label?.toLowerCase().includes(term) ||
            option.id?.toLowerCase().includes(term) ||
            option.tin?.toLowerCase().includes(term),
        );
    if (!term && searchable) {
      return list.slice(0, 5);
    }
    return list;
  }, [options, search, searchable]);

  const handleToggle = (id) => {
    if (disabled) {
      return;
    }
    const next = selectedSet.has(id) ? value.filter((item) => item !== id) : [...value, id];
    onChange(next);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  return (
    <>
      <Button
        type="button"
        size="small"
        variant="outlined"
        disabled={disabled}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 12 }} />}
        sx={{
          textTransform: 'none',
          height: 32,
          px: 1.25,
          gap: 0.5,
          fontSize: 12,
          fontWeight: 500,
          borderColor: MIS_UI.BORDER,
          bgcolor: MIS_UI.SURFACE_MUTED,
          color: MIS_UI.TEXT,
          '& .MuiButton-endIcon': { color: 'inherit' },
          '&.Mui-disabled': {
            bgcolor: MIS_UI.SURFACE_MUTED,
            color: 'text.disabled',
            borderColor: MIS_UI.BORDER,
            opacity: 1,
          },
          '&:hover': {
            borderColor: MIS_UI.BORDER,
            bgcolor: MIS_UI.SURFACE_HOVER,
            color: MIS_UI.TEXT_HOVER,
          },
          '&.Mui-disabled:hover': {
            bgcolor: MIS_UI.SURFACE_MUTED,
            color: 'text.disabled',
            borderColor: MIS_UI.BORDER,
          },
        }}
      >
        {label}
        {hasSelection ? ` (${value.length})` : ''}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 256, mt: 0.5, p: 1 } } }}
      >
        {searchable && (
          <TextField
            size="small"
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={m.filters?.search_placeholder ?? 'Search...'}
            sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        <Stack spacing={0.25} sx={{ maxHeight: 192, overflowY: 'auto' }}>
          {filteredOptions.length === 0 ? (
            <Box sx={{ px: 1, py: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                {m.filters?.no_options ?? 'No results'}
              </Typography>
            </Box>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selectedSet.has(option.id);
              const optionLabel = option.tin ? `${option.tin} | ${option.label}` : option.label;
              return (
                <Box
                  key={option.id}
                  component="button"
                  type="button"
                  onClick={() => handleToggle(option.id)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'block',
                    width: '100%',
                    boxSizing: 'border-box',
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {optionLabel}
                </Box>
              );
            })
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default MultiSelectFilterButton;
